import { useCollection, useDocument } from "@/composables/useFirestore";
import { db } from "@/firebase";
import type { TransferRequest } from "@/utils/types";
import type { Account } from "@aircap/common/account";
import type { SwanTransferRequest } from "@aircap/common/swan";
import {
  treezorTransferRequestsFullPath,
  type TransferRequest as TreezorTransferRequest,
} from "@aircap/common/treezor";
import { collection, doc, orderBy, query, where } from "firebase/firestore";
import { computed, type Ref } from "vue";
import { useTransferRequest } from "./useTransferRequest";

export type FormattedTransfer = TransferRequest & {
  id: string;
  translatedStatus: { title: string; tooltip: string | null };
  color: string;
  formattedAmount: string;
  formattedDate: string;
};

export const useTransferRequests = (
  accountId: Ref<string>,
  filter?: {
    beneficiaryIban?: Ref<string | null>;
    status?: Ref<TransferRequest["status"] | null>;
  },
) => {
  const { formatTransferRequest } = useTransferRequest({ accountId });
  const accountSource = computed(() => doc(collection<Account>(db, "accounts"), accountId.value));
  const { data: account } = useDocument<Account>(accountSource);

  const transferRequestsRef = computed(() => {
    switch (account.value?.accountType) {
      case "swan":
        return query(
          collection<SwanTransferRequest>(db, `accounts/${accountId.value}/swanTransferRequests`),
          ...(filter?.beneficiaryIban?.value
            ? [where<SwanTransferRequest>("beneficiaryIban", "==", filter.beneficiaryIban.value)]
            : []),
          ...(filter?.status?.value
            ? [where<SwanTransferRequest>("status", "==", filter.status.value)]
            : []),
          orderBy<SwanTransferRequest>("createdAt", "desc"),
        );
      case "treezor":
        return query(
          collection<TreezorTransferRequest>(db, treezorTransferRequestsFullPath(accountId.value)),
          ...(filter?.beneficiaryIban?.value
            ? [where<TreezorTransferRequest>("beneficiaryIban", "==", filter.beneficiaryIban.value)]
            : []),
          ...(filter?.status?.value
            ? [where<TreezorTransferRequest>("status", "==", filter.status.value)]
            : []),
          orderBy<TreezorTransferRequest>("createdAt", "desc"),
        );
      case "bridge":
      case "gcl":
      case "powens":
      case undefined:
        return undefined;
    }
  });

  const { data: rawTransferRequests, pending: loadingTransferRequests } = useCollection<
    SwanTransferRequest | TreezorTransferRequest
  >(transferRequestsRef);

  const transferRequests = computed(() => {
    return rawTransferRequests.value?.map((transferRequest) =>
      formatTransferRequest(transferRequest),
    );
  });

  return { transferRequests, loadingTransferRequests, account };
};
